<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import WikiText from '@/components/wikitext/WikiText.vue'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { urlFromAWSUrl } from '@/imagekit/imageurl_from_aws_url'
import StrategyEditButton from './StrategyEditButton.vue'
import type { StrategyUdlStandard } from '../strategy_types'
import { LIGHT_STRATEGY_TEXT } from '../strategy_text_classes'

const BANNER_IMAGE_TRANSFORMATION = [{ q: 50, w: 800 }]

const UDL_PRINCIPLE_CLASSES: Record<string, string> = {
  I: 'mr-0.5 bg-udl-representationDark',
  II: 'm-0.5 bg-udl-expressionDark',
  III: 'm-0.5 bg-udl-engagementDark'
}

const props = withDefaults(
  defineProps<{
    uuid: string
    title: string
    altNames?: string[]
    udlStandards: StrategyUdlStandard[]
    description: string
    imageUrl: string
  }>(),
  { altNames: () => [] }
)

const { editorVisibility } = storeToRefs(useVisibilityStore())

const bannerImageUrl = computed(() => urlFromAWSUrl(props.imageUrl, BANNER_IMAGE_TRANSFORMATION))
</script>

<template>
  <div class="relative mb-8 min-h-[300px] bg-black/70 pb-12 pt-6 text-text-md leading-[1.42857143] text-[#e2e2e2]">
    <div
      class="absolute inset-0 z-[-1] bg-cover bg-center bg-no-repeat"
      data-testid="strategy-banner-image"
      :style="{ backgroundImage: `url('${bannerImageUrl}')` }"
    />

    <StrategyEditButton v-if="editorVisibility" class="absolute right-3 top-3" :uuid="uuid" />

    <div class="px-8">
      <div class="pb-1 text-xl font-medium uppercase">Strategy</div>

      <h1 class="mb-2.5 mt-5 text-display-xl font-regular leading-[56px] text-white">{{ title }}</h1>

      <div class="pb-1 text-gray-400" data-testid="strategy-banner-alt-names">{{ altNames.join(', ') }}</div>

      <div class="pb-1">
        <span
          v-for="udl in udlStandards"
          :key="udl.udl_checkpoint"
          class="whitespace-nowrap rounded-[0.25em] px-1 py-0.5 text-center align-baseline text-text-sm font-bold leading-none text-white"
          :class="UDL_PRINCIPLE_CLASSES[udl.udl_principle]"
          >UDL {{ udl.udl_checkpoint }}</span
        >
      </div>

      <WikiText :class="['mt-8 text-[21px] leading-7 [&_p]:!mb-0 [&_p]:max-w-none [&_p]:pb-1', LIGHT_STRATEGY_TEXT]" :text="description" />
    </div>
  </div>
</template>
